import { memo, useState } from "react";
import { Box, Container, MenuItem, TextField, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MessageForm from "../../Components/MessageForm/MessageForm";
import {
  INQUIRY_TOPIC_LABELS,
  isInquiryTopic,
  type InquiryTopic,
} from "../../Services/api.contact.slice";

// The choices offered here; the rest are set by the page a message comes from.
const CONTACT_TOPICS: InquiryTopic[] = [
  "general",
  "work-with-us",
  "service",
  "find-home",
  "ownership",
  "premium",
  "broken-page",
  "feedback",
  "careers",
  "press",
  "privacy",
];

/**
 * /contact. Links elsewhere can preselect the topic and subject:
 * /contact?topic=press, /contact?topic=feedback&subject=Blog%20topic%20idea.
 */
const Contact = memo(() => {
  const [params] = useSearchParams();
  const requested = params.get("topic");
  const [topic, setTopic] = useState<InquiryTopic>(isInquiryTopic(requested) ? requested : "general");
  const subject = (params.get("subject") || "").slice(0, 200);

  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #004B4C 0%, #006668 100%)",
            borderRadius: { xs: "16px", md: "24px" },
            minHeight: { xs: 300, md: 400 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: { xs: 4, md: 8 },
            mb: { xs: 4, md: 6 },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: "#ffffff",
              fontWeight: 700,
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            Contact Us
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#ffffff",
              fontSize: { xs: "1rem", md: "1.2rem" },
              fontWeight: 300,
              lineHeight: 1.6,
              maxWidth: 600,
              opacity: 0.9,
            }}
          >
            Have questions or feedback? We'd love to hear from you. Get in touch
            with our team.
          </Typography>
        </Box>

        {/* Contact Info & Form Section */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
            gap: { xs: 4, md: 6 },
            py: { xs: 4, md: 6 },
          }}
        >
          {/* Contact Information */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                mb: 4,
              }}
            >
              Get in Touch
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <EmailIcon sx={{ color: "primary.main", mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Email
                  </Typography>
                  <Typography
                    variant="body1"
                    component="a"
                    href="mailto:info@simplerhome.app"
                    sx={{
                      color: "text.secondary",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    info@simplerhome.app
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <LocationOnIcon sx={{ color: "primary.main", mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Address
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    PO Box 1700
                    <br />
                    Studio City, CA 91614
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Contact Form */}
          <Box
            sx={{
              backgroundColor: "#f8f8f8",
              borderRadius: "16px",
              p: { xs: 3, md: 5 },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                mb: 3,
              }}
            >
              Send us a Message
            </Typography>

            <TextField
              select
              label="What's this about?"
              value={topic}
              onChange={(e) => setTopic(e.target.value as InquiryTopic)}
              fullWidth
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { backgroundColor: "#ffffff" } }}
              data-testid="contact-topic"
            >
              {CONTACT_TOPICS.map((t) => (
                <MenuItem key={t} value={t}>
                  {INQUIRY_TOPIC_LABELS[t]}
                </MenuItem>
              ))}
            </TextField>

            <MessageForm
              onTint
              topic={topic}
              showSubject
              showPhone={topic === "work-with-us" || topic === "service"}
              defaultSubject={subject}
            />
          </Box>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default Contact;
